// Stage is now the lead "temperature" (formerly Priority).
export const LEAD_STAGES = [
  { value: "hot", label: "Hot" },
  { value: "warm", label: "Warm" },
  { value: "cold", label: "Cold" },
];

export const STAGE_COLORS = {
  hot: "bg-red-100 text-red-700",
  warm: "bg-orange-100 text-orange-700",
  cold: "bg-gray-100 text-gray-600",
};

// Status is the pipeline the kanban board runs on (formerly Stage).
export const LEAD_STATUSES = [
  { value: "new_lead", label: "New Lead" },
  { value: "rnr", label: "RNR" },
  { value: "call_back", label: "Call Back" },
  { value: "appointment_booked", label: "Appointment Booked" },
  { value: "interested", label: "Interested" },
  { value: "number_busy", label: "Number Busy" },
  { value: "switched_off", label: "Switched Off" },
  { value: "visited", label: "Visited" },
  { value: "not_interested", label: "Not Interested" },
  { value: "wrong_number", label: "Wrong Number" },
  { value: "invalid_number", label: "Invalid Number" },
  { value: "location_constraint", label: "Location Constraint" },
];

// Dead-end statuses that close a lead — a follow-up is not required for these,
// even on a hot/warm lead. Moving a lead here auto-closes its pending follow-ups
// and POST /followups is rejected, so the UI hides all follow-up controls.
// MUST mirror backend core.STATUS_LOST exactly.
export const LOST_STATUSES = ["not_interested", "wrong_number", "invalid_number", "location_constraint"];

export const STATUS_COLORS = {
  new_lead: "bg-indigo-100 text-indigo-800",
  rnr: "bg-amber-100 text-amber-800",
  call_back: "bg-yellow-100 text-yellow-800",
  appointment_booked: "bg-purple-100 text-purple-800",
  interested: "bg-blue-100 text-blue-800",
  number_busy: "bg-amber-100 text-amber-800",
  switched_off: "bg-amber-100 text-amber-800",
  visited: "bg-green-100 text-green-800",
  not_interested: "bg-gray-200 text-gray-700",
  wrong_number: "bg-gray-100 text-gray-500",
  invalid_number: "bg-gray-100 text-gray-500",
  location_constraint: "bg-gray-200 text-gray-700",
};

export const SOURCES = [
  "Google Ads", "Facebook Lead Ads", "Instagram Ads", "Website Form",
  "WhatsApp", "Practo", "Justdial", "Referral", "Walk-in", "Camps/Events",
];

// Sources offered when booking an appointment for someone who ISN'T in the
// system yet — the booking creates their lead with this source. Kept short on
// purpose (it's answered in one beat at the desk). Mirrors backend
// core.BOOKING_SOURCES; the server rejects anything outside that list.
export const BOOKING_SOURCES = ["Google", "Walk-in", "Facebook", "Instagram"];

// The disposition (call-log) form offers only these stages, regardless of what
// else HQ adds to the master-data stage list. Matched with spaces/underscores
// collapsed and case ignored, so "Appointment booked" / "appointment_booked"
// and "VISITED" / "Appointment Visited" all qualify.
const DISPOSITION_STAGES = ["hot", "warm", "cold", "appointment booked", "visited", "appointment visited"];
export const isDispositionStage = (value) =>
  DISPOSITION_STAGES.includes(String(value || "").toLowerCase().replace(/[_\s]+/g, " ").trim());

export const LEAD_SUB_STATUSES = [
  "None", "Confirmed", "Rescheduled", "Cancelled", "Visited", "No Show",
];

export const LEAD_CATEGORIES = [
  "Dermatologists", "Cosmetologists", "Trichologists", "General",
];

export const LEAD_CONCERNS = [
  "SKIN", "HAIR", "NAILS", "BODY", "FACE", "ACNE", "PIGMENTATION",
  "ANTI_AGING", "LASER", "HAIR_FALL", "DANDRUFF", "OTHER",
];

export const LOST_REASONS = [
  "Budget Issue", "Not Interested", "Competitor Chosen", "Unreachable",
  "Duplicate", "Invalid Lead", "Wrong Requirement", "Delayed Decision",
];

// `manual: false` = the status is still a valid value (kept here so its badge
// renders), but users can't pick it from a status dropdown. "Completed" is set
// automatically when a doctor finishes a consultation, not chosen by hand —
// telecallers use Visited / No Show instead. Use APPOINTMENT_STATUS_OPTIONS for
// selectable dropdowns and APPOINTMENT_STATUS for label/colour lookup.
// Green means exactly one thing: done AND money captured (= visited). The
// doctor-finished state is cyan, so the day-of queue reads amber → indigo →
// cyan → green. Mirrors backend core.py APPOINTMENT_STATUS.
export const APPOINTMENT_STATUS = [
  { value: "scheduled", label: "Scheduled", color: "bg-blue-100 text-blue-800" },
  { value: "confirmed", label: "Confirmed", color: "bg-teal-100 text-teal-800" },
  { value: "ringing", label: "Ringing", color: "bg-purple-100 text-purple-800" },
  { value: "checked_in", label: "Checked-in", color: "bg-amber-100 text-amber-800" },
  { value: "in_consultation", label: "In Consultation", color: "bg-indigo-100 text-indigo-800" },
  { value: "checked_out", label: "At Checkout", color: "bg-cyan-100 text-cyan-800", manual: false },
  { value: "rescheduled", label: "Rescheduled", color: "bg-amber-100 text-amber-800" },
  { value: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-700" },
  { value: "visited", label: "Visited", color: "bg-green-100 text-green-800" },
  { value: "no_show", label: "No Show", color: "bg-gray-200 text-gray-600" },
  // Legacy value, kept for ONE release so a cached browser bundle doesn't render
  // migrated rows as "Scheduled" (the badge lookup falls back to index 0).
  // Delete once every client has the new bundle.
  { value: "completed", label: "Completed (legacy)", color: "bg-gray-100 text-gray-600", manual: false },
];

// Statuses a user may pick from a dropdown (excludes auto-only ones).
export const APPOINTMENT_STATUS_OPTIONS = APPOINTMENT_STATUS.filter((s) => s.manual !== false);

// Roles that may change an appointment's status — mirrors backend
// APPT_STATUS_ROLES (core.py) and the app's kApptStatusRoles. Includes
// telecaller: they book appointments from the call disposition and manage them
// on follow-up calls (confirm / reschedule / cancel / no-show). Lives here so
// the Appointments page and Lead Detail can't drift apart — they did once, and
// telecallers silently lost the status dropdown on the Appointments page.
export const APPT_STATUS_ROLES = [
  "super_admin", "hq_admin", "branch_manager", "franchisee_owner",
  "receptionist", "doctor", "telecaller",
];

// Statuses that only happen with the patient physically at the clinic — the
// front desk checks them in, the doctor takes them into consultation. A
// telecaller works the phone, so these are hidden from their status dropdown
// and they don't get the matching Check-in / Start / Resume buttons. They keep
// confirm / ringing / reschedule / cancel / no-show / visited.
// Mirrors the app's kClinicOnlyStatuses.
export const CLINIC_ONLY_STATUSES = ["checked_in", "in_consultation"];

// Status options `role` may pick from a dropdown. `current` is always kept in
// the list so a Select showing the appointment's existing status still renders
// it (a telecaller looking at an already-checked-in patient, say).
export function appointmentStatusOptionsFor(role, current = null) {
  if (role !== "telecaller") return APPOINTMENT_STATUS_OPTIONS;
  return APPOINTMENT_STATUS_OPTIONS.filter(
    (s) => s.value === current || !CLINIC_ONLY_STATUSES.includes(s.value)
  );
}

// Calendar card palette (Appointments day/month view + CalendarPage), tuned to
// the brand (navy + gold + nude). bar = left accent, head = solid header band,
// body = pale fill below. Lifted here from the two pages that had byte-identical
// copies of it. `ringing` deliberately has no entry — it falls back to scheduled.
export const APPOINTMENT_STATUS_CARD = {
  scheduled:       { bar: "#1B3A5C", head: "#F0E8DE", body: "#F8F4EE" }, // navy + nude
  confirmed:       { bar: "#C49A6C", head: "#F3E8D8", body: "#FBF6EF" }, // gold
  checked_in:      { bar: "#B45309", head: "#FDF1DC", body: "#FEF8EC" }, // amber (waiting)
  in_consultation: { bar: "#4338CA", head: "#E5E7FB", body: "#F1F2FD" }, // indigo (active)
  checked_out:     { bar: "#0891B2", head: "#DCF4F8", body: "#EFFAFC" }, // cyan (at desk)
  completed:       { bar: "#0891B2", head: "#DCF4F8", body: "#EFFAFC" }, // legacy → cyan
  rescheduled:     { bar: "#A98256", head: "#F3E8D8", body: "#FBF6EF" }, // gold-hover
  cancelled:       { bar: "#9CA3AF", head: "#EEEAE4", body: "#F6F3EE" }, // muted
  visited:         { bar: "#15803D", head: "#DDF3E4", body: "#EFFAF4" }, // green (done)
  no_show:         { bar: "#9CA3AF", head: "#EEEAE4", body: "#F6F3EE" }, // muted
};

export const cardColors = (status) => APPOINTMENT_STATUS_CARD[status] || APPOINTMENT_STATUS_CARD.scheduled;

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  hq_admin: "HQ Admin",
  branch_manager: "Branch Manager",
  franchisee_owner: "Franchisee Owner",
  telecaller: "Tele-caller",
  doctor: "Doctor / Consultant",
  receptionist: "Receptionist",
};

export const SERVICES = [
  "Laser Hair Removal", "Acne Treatment", "Hair Transplant", "Skin Whitening",
  "PRP Therapy", "Anti-Ageing", "Tattoo Removal", "Chemical Peel",
];

export const TREATMENT_STATUS = [
  { value: "started", label: "Started", color: "bg-blue-100 text-blue-800" },
  { value: "follow_up", label: "Follow-up", color: "bg-amber-100 text-amber-800" },
  { value: "completed", label: "Completed", color: "bg-green-100 text-green-800" },
];

// ---- Consultation / Clinical ----

export const DERMA_CONDITIONS = [
  "Acne Vulgaris", "Melasma", "Alopecia Areata", "Androgenetic Alopecia",
  "Psoriasis", "Eczema / Atopic Dermatitis", "Vitiligo", "Warts (Verruca)",
  "Fungal Infection (Tinea)", "Contact Dermatitis", "Urticaria",
  "Seborrheic Dermatitis", "Rosacea", "Lichen Planus", "Scabies",
  "Herpes Zoster", "Impetigo", "Photodermatitis", "Drug Eruption",
  "Hyperpigmentation", "Keloid / Hypertrophic Scar", "Moles (Nevi)",
  "Skin Tags", "Dandruff", "Hair Fall", "Other",
];

export const DERMA_DRUGS = [
  { name: "Isotretinoin", defaultDosage: "20mg", defaultFreq: "OD", route: "oral" },
  { name: "Minoxidil", defaultDosage: "5%", defaultFreq: "BD", route: "topical" },
  { name: "Adapalene", defaultDosage: "0.1%", defaultFreq: "OD", route: "topical" },
  { name: "Tretinoin", defaultDosage: "0.025%", defaultFreq: "OD", route: "topical" },
  { name: "Clindamycin", defaultDosage: "1%", defaultFreq: "BD", route: "topical" },
  { name: "Benzoyl Peroxide", defaultDosage: "2.5%", defaultFreq: "OD", route: "topical" },
  { name: "Hydroxychloroquine", defaultDosage: "200mg", defaultFreq: "BD", route: "oral" },
  { name: "Methotrexate", defaultDosage: "7.5mg", defaultFreq: "weekly", route: "oral" },
  { name: "Finasteride", defaultDosage: "1mg", defaultFreq: "OD", route: "oral" },
  { name: "Ketoconazole", defaultDosage: "2%", defaultFreq: "OD", route: "topical" },
  { name: "Tacrolimus", defaultDosage: "0.1%", defaultFreq: "BD", route: "topical" },
  { name: "Clobetasol", defaultDosage: "0.05%", defaultFreq: "BD", route: "topical" },
  { name: "Doxycycline", defaultDosage: "100mg", defaultFreq: "BD", route: "oral" },
  { name: "Azithromycin", defaultDosage: "500mg", defaultFreq: "OD", route: "oral" },
  { name: "Biotin", defaultDosage: "10mg", defaultFreq: "OD", route: "oral" },
  { name: "Sunscreen SPF 50", defaultDosage: "", defaultFreq: "BD", route: "topical" },
];

export const PRESCRIPTION_FREQUENCIES = [
  { value: "OD", label: "Once daily (OD)" },
  { value: "BD", label: "Twice daily (BD)" },
  { value: "TDS", label: "Thrice daily (TDS)" },
  { value: "QID", label: "Four times daily (QID)" },
  { value: "SOS", label: "As needed (SOS)" },
  { value: "HS", label: "At bedtime (HS)" },
  { value: "weekly", label: "Once weekly" },
  { value: "alternate", label: "Alternate days" },
];

export const DRUG_ROUTES = [
  { value: "oral", label: "Oral" },
  { value: "topical", label: "Topical" },
  { value: "injection", label: "Injection" },
  { value: "inhalation", label: "Inhalation" },
];

export const FITZPATRICK_TYPES = [
  { value: "I", label: "Type I — Always burns, never tans" },
  { value: "II", label: "Type II — Burns easily, tans minimally" },
  { value: "III", label: "Type III — Burns moderately, tans gradually" },
  { value: "IV", label: "Type IV — Burns minimally, tans well" },
  { value: "V", label: "Type V — Rarely burns, tans darkly" },
  { value: "VI", label: "Type VI — Never burns, deeply pigmented" },
];

export const BODY_AREAS = [
  "Face", "Scalp", "Neck", "Chest", "Back", "Arms",
  "Hands", "Legs", "Feet", "Abdomen", "Groin", "Full Body",
];

export const DRUG_INSTRUCTIONS = [
  { value: "before_food", label: "Before food" },
  { value: "after_food", label: "After food" },
  { value: "with_food", label: "With food" },
  { value: "empty_stomach", label: "Empty stomach" },
  { value: "at_bedtime", label: "At bedtime" },
  { value: "as_directed", label: "As directed" },
];

export function stageLabel(v) {
  if (!v) return v;
  const lv = v.toLowerCase();
  return (LEAD_STAGES.find((s) => s.value.toLowerCase() === lv) || {}).label || v;
}

export function statusLabel(v) {
  if (!v) return v;
  const lv = v.toLowerCase();
  return (LEAD_STATUSES.find((s) => s.value.toLowerCase() === lv) || {}).label || v;
}

export function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";  // unparseable (e.g. legacy import dates)
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

// Doctors are often stored with the title already in their name ("Dr. Asha
// Rao"), so prefixing unconditionally produced "Dr. Dr. Asha Rao". Same rule
// the prescription PDF applies server-side (pdf_prescription.py).
export function withDrPrefix(name) {
  const n = (name || "").trim();
  if (!n) return "";
  return n.toLowerCase().startsWith("dr") ? n : `Dr. ${n}`;
}

export function formatTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

export function formatCurrency(n) {
  return "₹" + (n || 0).toLocaleString("en-IN");
}

/* ── Follow-up utilities ────────────────────────────────────────────────── */

export function smartDueLabel(iso) {
  if (!iso) return "—";
  const now = new Date();
  const due = new Date(iso);
  const diffMs = due - now;
  const diffMin = Math.round(diffMs / 60000);
  const diffHrs = Math.round(diffMs / 3600000);
  const diffDays = Math.round(diffMs / 86400000);
  const time = due.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  const isToday = due.toDateString() === now.toDateString();
  const isTomorrow = due.toDateString() === new Date(now.getTime() + 86400000).toDateString();

  if (diffMs < 0) {
    const ago = Math.abs(diffDays);
    if (ago === 0) return `Overdue by ${Math.abs(diffHrs)}h`;
    return `Overdue by ${ago} day${ago !== 1 ? "s" : ""}`;
  }
  if (isToday) {
    if (diffMin < 60) return `In ${diffMin}m (${time})`;
    return `In ${diffHrs}h (${time} today)`;
  }
  if (isTomorrow) return `Tomorrow at ${time}`;
  if (diffDays <= 7) return `In ${diffDays} days (${due.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })})`;
  return formatDate(iso);
}

export function smartDueUrgency(iso) {
  if (!iso) return "muted";
  const diffMs = new Date(iso) - new Date();
  if (diffMs < 0) return "overdue";
  if (diffMs < 86400000) return "today";
  return "future";
}

export function groupFollowUpsByUrgency(items) {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const immediate = [];
  const today = [];
  const upcoming = [];
  const completed = [];

  for (const f of items) {
    if (f.status === "done") { completed.push(f); continue; }
    if (f.status === "overdue" || (f.priority === "urgent" && f.status !== "done")) {
      immediate.push(f);
    } else if (f.due_at?.slice(0, 10) === todayStr) {
      today.push(f);
    } else {
      upcoming.push(f);
    }
  }
  immediate.sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
  today.sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
  upcoming.sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
  completed.sort((a, b) => new Date(b.due_at) - new Date(a.due_at));
  return { immediate, today, upcoming, completed: completed.slice(0, 20) };
}

export const FOLLOWUP_PRESETS = [
  { label: "Quick Call Back", title: "Call back", type: "call_back", channel: "call", priority: "normal", offsetHours: 2 },
  { label: "Treatment Follow-up", title: "Post-treatment check", type: "post_visit", channel: "call", priority: "high", offsetHours: 72 },
  { label: "Payment Reminder", title: "Payment follow-up", type: "payment_reminder", channel: "whatsapp", priority: "normal", offsetHours: 48 },
  { label: "Re-engagement", title: "Re-engagement check-in", type: "re_engagement", channel: "call", priority: "normal", offsetHours: 168 },
];

export const CHANNEL_OPTIONS = [
  { value: "call", label: "Call", icon: "Phone" },
  { value: "whatsapp", label: "WhatsApp", icon: "MessageCircle" },
  { value: "email", label: "Email", icon: "Mail" },
  { value: "in_app", label: "In-app", icon: "Bell" },
];
